import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api, { endpoints } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Grid,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Lock as LockIcon,
  Add as AddIcon,
  ArrowBack as BackIcon,
  LocalShipping as PalletIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  CheckCircle as FreeIcon,
  Cancel as OccupiedIcon,
  Close as CloseIcon,
  Label as LabelIcon,
  AdminPanelSettings as AdminIcon,
  Timeline as TrackingIcon,
} from '@mui/icons-material';

const VueloGestion = () => {
  const { awb } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [vuelo, setVuelo] = useState(null);
  const [posiciones, setPosiciones] = useState([]);
  const [pallets, setPallets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPalletDialog, setShowPalletDialog] = useState(false);
  const [selectedPosicion, setSelectedPosicion] = useState(null);
  const [selectedPallet, setSelectedPallet] = useState(null);
  const [dialogMode, setDialogMode] = useState('add'); // 'add' | 'edit'

  useEffect(() => {
    fetchData();
  }, [awb]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const vueloRes = await api.get(`${endpoints.vuelos.detalle}?awb=${awb}`);
      const vueloData = vueloRes.data.data.vuelo;

      if (vueloData.cerrado == 1) {
        navigate(`/vuelos/${awb}/detalle`);
        return;
      }

      setVuelo(vueloData);

      const posRes = await api.get(
        `${endpoints.posiciones.list}?equipo_id=${vueloData.equipo_id}&awb=${awb}`
      );
      setPosiciones(posRes.data.data.posiciones);

      const palletsRes = await api.get(`${endpoints.pallets.list}?awb=${awb}`);
      setPallets(palletsRes.data.data.pallets);
    } catch (error) {
      console.error('Error al cargar datos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCerrarVuelo = async () => {
    if (!confirm('¿Está seguro de cerrar este vuelo?')) return;

    try {
      await api.post(endpoints.vuelos.cerrar, { awb });
      navigate(`/vuelos/${awb}/detalle`);
    } catch (error) {
      alert('Error al cerrar el vuelo: ' + (error.response?.data?.message || error.message));
    }
  };

  const handlePosicionClick = (posicion) => {
    if (posicion.estado === 'libre') {
      setSelectedPosicion(posicion);
      setSelectedPallet(null);
      setDialogMode('add');
      setShowPalletDialog(true);
    } else {
      const pallet = pallets.find((p) => p.id_posicion === posicion.posicion_id);
      if (pallet) {
        if (hasPermission('vuelos.pallets.editar')) {
          // Puede editar si tiene permiso
          setSelectedPosicion(posicion);
          setSelectedPallet(pallet);
          setDialogMode('edit');
          setShowPalletDialog(true);
        } else {
          // Solo puede ver info
          alert(`Placa: ${pallet.placa}\nPeso: ${pallet.pesoTotalP} kg\nGuías: ${pallet.guias?.join(', ')}`);
        }
      }
    }
  };

  const handleEditPallet = (pallet) => {
    const posicion = posiciones.find((p) => p.posicion_id === pallet.id_posicion);
    setSelectedPosicion(posicion);
    setSelectedPallet(pallet);
    setDialogMode('edit');
    setShowPalletDialog(true);
  };

  const handleDeletePallet = async (pallet) => {
    if (!confirm(`¿Está seguro de eliminar el pallet ${pallet.placa}?`)) return;

    try {
      await api.delete(`${endpoints.pallets.delete}?id=${pallet.id}`);
      fetchData(); // Recargar datos
    } catch (error) {
      alert('Error al eliminar: ' + (error.response?.data?.message || error.message));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (!vuelo) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h5">Vuelo no encontrado</Typography>
      </Box>
    );
  }

  const totalKilos = pallets.reduce((sum, p) => sum + (parseFloat(p.pesoTotalP) || 0), 0);
  const porcentaje = vuelo.maxload > 0 ? (totalKilos / vuelo.maxload) * 100 : 0;
  const disponible = vuelo.maxload - totalKilos;
  const posicionesLibres = posiciones.filter((p) => p.estado === 'libre').length;

  // Verificar si es vuelo multi-destino
  const vueloDestinos = vuelo.Destino?.split(';').filter(d => d.trim()) || [];
  const isMultiDestino = vueloDestinos.length > 1;
  
  // Colores para diferentes destinos (paleta distintiva)
  const destinoColors = {
    // Colores primarios
    0: { bg: '#1976D2', hover: '#1565C0', name: 'blue' },        // Azul
    1: { bg: '#D32F2F', hover: '#C62828', name: 'red' },         // Rojo
    2: { bg: '#388E3C', hover: '#2E7D32', name: 'green' },       // Verde
    3: { bg: '#F57C00', hover: '#E65100', name: 'orange' },      // Naranja
    4: { bg: '#7B1FA2', hover: '#6A1B9A', name: 'purple' },      // Púrpura
    5: { bg: '#00838F', hover: '#006064', name: 'cyan' },        // Cyan
    6: { bg: '#C2185B', hover: '#AD1457', name: 'pink' },        // Rosa
    7: { bg: '#5D4037', hover: '#4E342E', name: 'brown' },       // Marrón
  };

  // Crear mapa de destino -> color basado en los destinos del vuelo
  const destinoColorMap = {};
  vueloDestinos.forEach((destino, index) => {
    destinoColorMap[destino.trim()] = destinoColors[index % Object.keys(destinoColors).length];
  });

  // Obtener color para un pallet basado en su destino
  const getPalletColor = (pallet) => {
    if (!pallet || !isMultiDestino) {
      return { bg: '#D32F2F', hover: '#C62828' }; // Rojo por defecto
    }
    const destino = pallet.dfinal || pallet.destino || '';
    return destinoColorMap[destino] || { bg: '#9E9E9E', hover: '#757575' }; // Gris si no tiene destino
  };

  // Obtener destinos únicos de los pallets cargados
  const destinosEnUso = [...new Set(pallets.map(p => p.dfinal || p.destino).filter(d => d))];

  const grid = {};
  let maxFila = 0;
  let maxColumna = 0;
  posiciones.forEach((pos) => {
    if (!grid[pos.fila]) grid[pos.fila] = {};
    grid[pos.fila][pos.columna] = pos;
    maxFila = Math.max(maxFila, pos.fila);
    maxColumna = Math.max(maxColumna, pos.columna);
  });

  const getProgressColor = () => {
    if (porcentaje > 95) return 'error';
    if (porcentaje > 90) return 'warning';
    return 'success';
  };

  const StatCard = ({ value, label, color }) => (
    <Card sx={{ borderLeft: 4, borderColor: `${color}.main`, height: '100%' }}>
      <CardContent sx={{ py: 2 }}>
        <Typography variant="h5" fontWeight="bold">
          {value}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      </CardContent>
    </Card>
  );

  return (
    <Box>
      {/* Header */}
      <Card
        sx={{
          mb: 3,
          background: 'linear-gradient(135deg, #1565C0 0%, #0D47A1 100%)',
          color: 'white',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Gestionar Vuelo: {vuelo.awb}
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap', opacity: 0.9 }}>
                <Typography>
                  Equipo: <strong>{vuelo.equipo_nombre}</strong>
                </Typography>
                <Typography>
                  Destino: <strong>{vuelo.Destino}</strong>
                </Typography>
                <Typography>
                  Fecha: <strong>{new Date(vuelo.fecha).toLocaleDateString()}</strong>
                </Typography>
              </Box>
            </Box>
            {hasPermission('vuelos.cerrar') && (
              <Button
                variant="contained"
                color="error"
                startIcon={<LockIcon />}
                onClick={handleCerrarVuelo}
                sx={{ fontWeight: 'bold' }}
              >
                Cerrar Vuelo
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard value={pallets.length} label="Pallets Cargados" color="primary" />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard value={posicionesLibres} label="Posiciones Libres" color="success" />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard value={`${totalKilos.toLocaleString()} kg`} label="Peso Cargado" color="secondary" />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard value={`${disponible.toLocaleString()} kg`} label="Disponible" color="warning" />
        </Grid>
        <Grid item xs={6} sm={4} md={2.4}>
          <StatCard value={`${porcentaje.toFixed(1)}%`} label="Capacidad Usada" color={getProgressColor()} />
        </Grid>
      </Grid>

      {/* Progress Bar */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2">Capacidad de Carga</Typography>
            <Typography variant="body2">
              {totalKilos.toLocaleString()} / {vuelo.maxload?.toLocaleString()} kg
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(porcentaje, 100)}
            color={getProgressColor()}
            sx={{ height: 12, borderRadius: 6 }}
          />
        </CardContent>
      </Card>

      {/* Grid de Posiciones */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" gutterBottom>
            Configuración del Equipo - {vuelo.equipo_nombre}
          </Typography>

          <Box sx={{ display: 'flex', gap: 3, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ width: 16, height: 16, bgcolor: 'success.main', borderRadius: 1 }} />
              <Typography variant="body2">Libre</Typography>
            </Box>
            {isMultiDestino ? (
              // Mostrar colores por destino cuando es multi-destino
              <>
                <Typography variant="body2" fontWeight="bold" sx={{ ml: 2, mr: 1 }}>
                  Destinos:
                </Typography>
                {vueloDestinos.map((destino, index) => {
                  const color = destinoColors[index % Object.keys(destinoColors).length];
                  const enUso = destinosEnUso.includes(destino.trim());
                  return (
                    <Box key={destino} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box sx={{ 
                        width: 16, 
                        height: 16, 
                        bgcolor: color.bg, 
                        borderRadius: 1,
                        opacity: enUso ? 1 : 0.5,
                      }} />
                      <Typography variant="body2" sx={{ opacity: enUso ? 1 : 0.6 }}>
                        {destino.trim()}
                        {enUso && (
                          <Chip 
                            label={pallets.filter(p => (p.dfinal || p.destino) === destino.trim()).length} 
                            size="small" 
                            sx={{ ml: 0.5, height: 18, fontSize: '0.7rem' }}
                          />
                        )}
                      </Typography>
                    </Box>
                  );
                })}
              </>
            ) : (
              // Vista simple para destino único
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ width: 16, height: 16, bgcolor: 'error.main', borderRadius: 1 }} />
                <Typography variant="body2">Ocupado</Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ overflowX: 'auto' }}>
            <Box sx={{ display: 'inline-block', minWidth: 'fit-content' }}>
              {Array.from({ length: maxFila }, (_, i) => i + 1).map((fila) => (
                <Box key={fila} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  {Array.from({ length: maxColumna }, (_, j) => j + 1).map((columna) => {
                    const pos = grid[fila]?.[columna];
                    if (!pos) {
                      return (
                        <Box
                          key={`${fila}-${columna}`}
                          sx={{
                            width: 80,
                            height: 88,
                            bgcolor: 'grey.100',
                            borderRadius: 1,
                            border: 1,
                            borderColor: 'grey.300',
                          }}
                        />
                      );
                    }

                    const isOcupado = pos.estado === 'ocupado';
                    
                    // Obtener el pallet en esta posición para determinar el color
                    const palletEnPos = isOcupado ? pallets.find(p => p.id_posicion === pos.posicion_id) : null;
                    const palletColor = isOcupado && isMultiDestino ? getPalletColor(palletEnPos) : null;
                    const palletDestino = palletEnPos?.dfinal || palletEnPos?.destino || '';

                    return (
                      <Tooltip
                        key={pos.posicion_id}
                        title={isOcupado 
                          ? `${pos.placa} - ${pos.pesoTotalP}kg${palletDestino ? ` → ${palletDestino}` : ''}` 
                          : 'Click para agregar pallet'
                        }
                      >
                        <Button
                          onClick={() => handlePosicionClick(pos)}
                          sx={{
                            width: 80,
                            height: 88,
                            minWidth: 80,
                            flexDirection: 'column',
                            borderRadius: 1,
                            border: 2,
                            bgcolor: isOcupado 
                              ? (isMultiDestino && palletColor ? palletColor.bg : 'error.main') 
                              : 'success.main',
                            borderColor: isOcupado 
                              ? (isMultiDestino && palletColor ? palletColor.hover : 'error.dark') 
                              : 'success.dark',
                            color: 'white',
                            '&:hover': {
                              bgcolor: isOcupado 
                                ? (isMultiDestino && palletColor ? palletColor.hover : 'error.dark') 
                                : 'success.dark',
                              transform: 'scale(1.05)',
                            },
                            transition: 'all 0.2s',
                          }}
                        >
                          <Typography variant="caption" fontWeight="bold">
                            {pos.tipo_posicion}
                          </Typography>
                          {isOcupado && (
                            <>
                              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                                {pos.placa}
                              </Typography>
                              {isMultiDestino && palletDestino && (
                                <Typography variant="caption" sx={{ fontSize: '0.6rem', opacity: 0.9 }}>
                                  → {palletDestino}
                                </Typography>
                              )}
                              <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                                {pos.pesoTotalP} kg
                              </Typography>
                            </>
                          )}
                        </Button>
                      </Tooltip>
                    );
                  })}
                </Box>
              ))}
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Lista de Pallets */}
      <Card sx={{ mb: 3 }}>
        <Box sx={{ p: 2, bgcolor: 'grey.50', borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="h6" fontWeight="bold">
            Pallets Cargados ({pallets.length})
          </Typography>
        </Box>
        {pallets.length > 0 ? (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Placa</TableCell>
                  <TableCell>Posición</TableCell>
                  {isMultiDestino && <TableCell>Destino</TableCell>}
                  <TableCell>Guías</TableCell>
                  <TableCell align="center">Piezas</TableCell>
                  <TableCell align="center">Peso</TableCell>
                  <TableCell align="center">DG</TableCell>
                  <TableCell align="center">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pallets.map((pallet) => (
                  <TableRow key={pallet.placa} hover>
                    <TableCell>
                      <Typography fontWeight="medium">{pallet.placa}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={pallet.tipoPos} size="small" variant="outlined" />
                    </TableCell>
                    {isMultiDestino && (
                      <TableCell>
                        {(pallet.dfinal || pallet.destino) ? (
                          <Chip 
                            label={pallet.dfinal || pallet.destino} 
                            size="small" 
                            sx={{ 
                              bgcolor: getPalletColor(pallet).bg,
                              color: 'white',
                              fontWeight: 'bold',
                            }}
                          />
                        ) : '-'}
                      </TableCell>
                    )}
                    <TableCell>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {pallet.guias?.slice(0, 3).map((guia, idx) => (
                          <Chip
                            key={idx}
                            label={guia}
                            size="small"
                            variant="outlined"
                            onClick={() => window.open(`/warehouse/tracking?awb=${guia}`, '_blank')}
                            onDelete={() => window.open(`/warehouse/tracking?awb=${guia}`, '_blank')}
                            deleteIcon={<TrackingIcon fontSize="small" />}
                            sx={{ cursor: 'pointer' }}
                          />
                        ))}
                        {pallet.guias?.length > 3 && (
                          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                            +{pallet.guias.length - 3} más
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">{pallet.total_piezas}</TableCell>
                    <TableCell align="center">
                      <Typography fontWeight="medium">{(pallet.pesoTotalP || 0).toLocaleString()} kg</Typography>
                    </TableCell>
                    <TableCell align="center">
                      {pallet.dg ? (
                        <Chip icon={<WarningIcon />} label="DG" size="small" color="warning" />
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        <Tooltip title="Ver Etiqueta">
                          <IconButton
                            component={Link}
                            to={`/vuelos/${awb}/pallet/${pallet.placa}/etiqueta`}
                            size="small"
                            color="primary"
                          >
                            <LabelIcon />
                          </IconButton>
                        </Tooltip>
                        {hasPermission('vuelos.pallets.editar') && (
                          <Tooltip title="Editar Pallet">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleEditPallet(pallet)}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('vuelos.pallets.eliminar') && (
                          <Tooltip title="Eliminar Pallet">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeletePallet(pallet)}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        ) : (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <PalletIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
            <Typography color="text.secondary">
              No hay pallets cargados. Haga clic en una posición libre para agregar.
            </Typography>
          </Box>
        )}
      </Card>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        {hasPermission('vuelos.pallets.crear') && (
          <Button
            component={Link}
            to={`/vuelos/${awb}/nuevo-pallet`}
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
          >
            Agregar Pallet
          </Button>
        )}
        <Button component={Link} to="/vuelos" variant="contained" color="inherit" startIcon={<BackIcon />}>
          Volver
        </Button>
      </Box>

      {/* Dialog para agregar/editar pallet */}
      <Dialog open={showPalletDialog} onClose={() => setShowPalletDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {dialogMode === 'edit' && <AdminIcon color="warning" />}
          {dialogMode === 'edit' 
            ? `Editar pallet ${selectedPallet?.placa}` 
            : `Nueva carga en posición ${selectedPosicion?.tipo_posicion}`}
          <IconButton
            onClick={() => setShowPalletDialog(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Typography color="text.secondary" sx={{ mb: 2 }}>
            Posición: {selectedPosicion?.descripcion || selectedPallet?.tipoPos}
          </Typography>
          
          {dialogMode === 'edit' && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <strong>Modo Admin:</strong> Está editando un pallet ya cargado.
            </Alert>
          )}

          {dialogMode === 'edit' && selectedPallet && (
            <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Placa:</strong> {selectedPallet.placa}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Peso:</strong> {selectedPallet.pesoTotalP} kg
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Piezas:</strong> {selectedPallet.total_piezas}
              </Typography>
              <Box>
                <Typography variant="body2" component="span"><strong>Guías:</strong></Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {selectedPallet.guias?.map((guia, idx) => (
                    <Chip
                      key={idx}
                      label={guia}
                      size="small"
                      variant="outlined"
                      component={Link}
                      to={`/warehouse/tracking?awb=${guia}`}
                      clickable
                      icon={<TrackingIcon fontSize="small" />}
                    />
                  )) || '-'}
                </Box>
              </Box>
            </Box>
          )}

          <Alert severity="info">
            {dialogMode === 'edit'
              ? 'Se abrirá el formulario para modificar este pallet.'
              : 'Se abrirá el formulario para registrar un nuevo pallet en esta posición.'}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPalletDialog(false)}>Cancelar</Button>
          <Button
            component={Link}
            to={dialogMode === 'edit' 
              ? `/vuelos/${awb}/editar-pallet/${selectedPallet?.id}?posicion=${selectedPosicion?.posicion_id || selectedPallet?.id_posicion}`
              : `/vuelos/${awb}/nuevo-pallet?posicion=${selectedPosicion?.posicion_id}`}
            variant="contained"
            color={dialogMode === 'edit' ? 'warning' : 'primary'}
          >
            {dialogMode === 'edit' ? 'Editar Pallet' : 'Continuar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VueloGestion;

